import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * mode === 'licensing' 일 때 강제 표시되는 라이선스 활성화 화면.
 * 흐름:
 *   1. license_key 입력 → /api/license/challenge → base64 challenge
 *   2. 사용자가 challenge 를 본사 라이선스 사이트에 입력 (별도 머신/탭 OK)
 *   3. 받은 activation_token 을 아래에 붙여넣고 /api/license/install
 *   4. 성공 시 mode=normal 진입 → /login redirect
 */
export function Licensing() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const status = useQuery({
    queryKey: ['license-status'],
    queryFn: api.licenseStatus,
    refetchOnWindowFocus: false,
  });

  const [licenseKey, setLicenseKey] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [hwid, setHwid] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);

  const challengeMut = useMutation({
    mutationFn: () => api.licenseChallenge({ licenseKey: licenseKey.trim().toUpperCase() }),
    onSuccess: (res) => {
      setChallenge(res.challenge);
      setHwid(res.hwid);
    },
  });

  const installMut = useMutation({
    mutationFn: () => api.licenseInstall({ token: token.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['onboarding-status'] });
      await qc.invalidateQueries({ queryKey: ['license-status'] });
      navigate({ to: '/login' });
    },
  });

  async function copyChallenge() {
    if (!challenge) return;
    await navigator.clipboard.writeText(challenge);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm border border-neutral-200 p-8 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">라이선스 활성화 필요</h1>
          <p className="mt-2 text-sm text-neutral-600">
            innermate 가 정상 동작하려면 본사에서 발급한 라이선스 활성화가 필요합니다.
          </p>
          {status.data?.reason && (
            <p className="mt-2 text-xs text-amber-700">
              현재 상태: <code className="font-mono">{status.data.reason}</code>
            </p>
          )}
          {hwid && (
            <p className="mt-2 text-xs text-neutral-500">
              이 서버 HWID:{' '}
              <code className="px-1.5 py-0.5 bg-neutral-100 rounded font-mono">
                {hwid.slice(0, 16)}…
              </code>
            </p>
          )}
        </header>

        {/* Step 1: license_key + challenge 생성 */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-sm font-semibold rounded-full">
              1
            </span>
            <h2 className="text-base font-semibold">라이선스 키 입력</h2>
          </div>
          <p className="text-sm text-neutral-600">본사에서 받은 라이선스 키 (INMT-...) 입력.</p>
          <input
            type="text"
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value)}
            placeholder="INMT-XXXX-XXXX-XXXX-XXXX-XXXX"
            className="w-full px-3 py-2 font-mono text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={() => challengeMut.mutate()}
            disabled={!licenseKey.trim() || challengeMut.isPending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 text-white text-sm font-medium rounded-md"
          >
            {challengeMut.isPending ? '생성 중…' : '활성화 코드 생성'}
          </button>
          {challengeMut.error && (
            <p className="text-sm text-red-700">실패: {(challengeMut.error as Error).message}</p>
          )}
        </section>

        {/* Step 2: challenge 표시 + 본사 사이트 link */}
        {challenge && (
          <section className="space-y-3 pt-4 border-t border-neutral-100">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-sm font-semibold rounded-full">
                2
              </span>
              <h2 className="text-base font-semibold">활성화 코드 → 본사 사이트</h2>
            </div>
            <p className="text-sm text-neutral-600">
              아래 코드를 복사한 뒤, 인터넷이 되는 머신에서 본사 라이선스 사이트에 입력하세요.
            </p>
            <textarea
              value={challenge}
              readOnly
              rows={5}
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-xs bg-neutral-50 border border-neutral-300 rounded-md"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyChallenge}
                className="px-3 py-1.5 bg-neutral-800 hover:bg-neutral-900 text-white text-sm rounded-md"
              >
                {copied ? '복사됨!' : '클립보드에 복사'}
              </button>
            </div>
          </section>
        )}

        {/* Step 3: token install */}
        {challenge && (
          <section className="space-y-3 pt-4 border-t border-neutral-100">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-sm font-semibold rounded-full">
                3
              </span>
              <h2 className="text-base font-semibold">활성화 토큰 설치</h2>
            </div>
            <p className="text-sm text-neutral-600">
              본사 사이트에서 받은 activation_token 을 붙여넣고 설치.
            </p>
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              rows={5}
              spellCheck={false}
              placeholder="eyJhbGciOiJFZERTQSIs..."
              className="w-full px-3 py-2 font-mono text-xs bg-white border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => installMut.mutate()}
              disabled={!token.trim() || installMut.isPending}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 text-white text-sm font-medium rounded-md"
            >
              {installMut.isPending ? '설치 중…' : '설치 + 라이선스 적용'}
            </button>
            {installMut.error && (
              <p className="text-sm text-red-700">실패: {(installMut.error as Error).message}</p>
            )}
            {installMut.data?.slot === 'pending' && (
              <p className="text-sm text-amber-700">
                현재 활성 라이선스가 있어 다음 큐에 등록되었습니다. 만료 시 자동으로 전환됩니다.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
